/**
 * MEGATOKE — theme.js
 * Shared runtime: namespace, utilities, lazy-init helper, section rendering API.
 * Loaded with `defer` from layout/theme.liquid. No dependencies.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Namespace
     ------------------------------------------------------------------------ */

  const Megatoke = {
    /** Breakpoints — keep in sync with assets/base.css. */
    breakpoints: {
      tablet: 750,
      desktop: 990,
      wide: 1400,
    },
    utils: {},
    cart: {},
  };

  window.Megatoke = Megatoke;

  /* ------------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------------ */

  /**
   * Trailing-edge debounce.
   * @param {Function} fn
   * @param {number} [wait=200]
   * @returns {Function} Debounced fn with a .cancel() method.
   */
  function debounce(fn, wait) {
    let timer = null;
    const delay = typeof wait === 'number' ? wait : 200;

    function debounced(...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn.apply(this, args);
      }, delay);
    }

    debounced.cancel = function () {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    return debounced;
  }

  /**
   * Leading-edge throttle, rAF-aligned. For scroll/resize handlers.
   * @param {Function} fn
   * @returns {Function}
   */
  function rafThrottle(fn) {
    let queued = false;
    let lastArgs = null;

    return function (...args) {
      lastArgs = args;
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn.apply(this, lastArgs);
      });
    };
  }

  /** @returns {boolean} True when the visitor asked for reduced motion. */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {'tablet'|'desktop'|'wide'} name
   * @returns {boolean} True at or above the named breakpoint.
   */
  function mediaUp(name) {
    const px = Megatoke.breakpoints[name];
    if (!px) return false;
    return window.matchMedia(`(min-width: ${px}px)`).matches;
  }

  Megatoke.utils.debounce = debounce;
  Megatoke.utils.rafThrottle = rafThrottle;
  Megatoke.utils.prefersReducedMotion = prefersReducedMotion;
  Megatoke.utils.mediaUp = mediaUp;

  /* ------------------------------------------------------------------------
     Lazy init — <is-land on="visible|idle|interaction|media">
     Defers work until the island is actually needed. Children stay in a
     <template> until hydration, so nothing inside them costs anything upfront.
     ------------------------------------------------------------------------ */

  class IsLand extends HTMLElement {
    connectedCallback() {
      if (this.hydrated) return;
      this.hydrated = false;

      const on = (this.getAttribute('on') || 'visible').trim();

      switch (on) {
        case 'idle':
          this.onIdle();
          break;
        case 'interaction':
          this.onInteraction();
          break;
        case 'media':
          this.onMedia();
          break;
        default:
          this.onVisible();
      }
    }

    onVisible() {
      const margin = this.getAttribute('root-margin') || '200px';

      if (!('IntersectionObserver' in window)) {
        this.hydrate();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.hydrate();
        },
        { rootMargin: margin }
      );

      observer.observe(this);
    }

    onIdle() {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.hydrate(), { timeout: 3000 });
      } else {
        setTimeout(() => this.hydrate(), 1);
      }
    }

    onInteraction() {
      const events = (this.getAttribute('events') || 'pointerenter,focusin,click').split(',');
      const once = { once: true, passive: true };

      const fire = () => {
        events.forEach((name) => this.removeEventListener(name.trim(), fire));
        this.hydrate();
      };

      events.forEach((name) => this.addEventListener(name.trim(), fire, once));
    }

    onMedia() {
      const query = this.getAttribute('query');
      if (!query) {
        this.hydrate();
        return;
      }

      const mql = window.matchMedia(query);
      if (mql.matches) {
        this.hydrate();
        return;
      }

      const onChange = (event) => {
        if (!event.matches) return;
        mql.removeEventListener('change', onChange);
        this.hydrate();
      };

      mql.addEventListener('change', onChange);
    }

    /** Swap the deferred <template> in and announce readiness. */
    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;

      const template = this.querySelector(':scope > template[data-island]');
      if (template) {
        this.replaceChildren(template.content.cloneNode(true));
      }

      this.setAttribute('hydrated', '');
      this.dispatchEvent(new CustomEvent('island:hydrated', { bubbles: true }));
    }
  }

  if (!customElements.get('is-land')) {
    customElements.define('is-land', IsLand);
  }

  /* ------------------------------------------------------------------------
     Section Rendering API
     Fetches re-rendered section markup so cart updates never reload the page.
     ------------------------------------------------------------------------ */

  /**
   * Fetch one or more sections' rendered HTML.
   * @param {string[]} sectionIds
   * @param {string} [url] Defaults to the current path.
   * @returns {Promise<Object<string,string>>} sectionId -> HTML string.
   */
  async function fetchSections(sectionIds, url) {
    const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
    const base = url || window.location.pathname;
    const endpoint = `${base}?sections=${encodeURIComponent(ids.join(','))}`;

    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Section render failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Replace the live DOM for a section with freshly rendered markup.
   * Matches on [data-section-target] so a section can swap only its inner
   * region and leave wrappers (and their scroll position) intact.
   * @param {string} sectionId
   * @param {string} html
   */
  function renderSection(sectionId, html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const selector = `[data-section-id="${sectionId}"]`;
    const incoming = parsed.querySelector(selector);
    const current = document.querySelector(selector);

    if (!incoming || !current) return;

    const targets = current.querySelectorAll('[data-section-target]');

    if (targets.length === 0) {
      current.innerHTML = incoming.innerHTML;
      return;
    }

    targets.forEach((target) => {
      const name = target.getAttribute('data-section-target');
      const source = incoming.querySelector(`[data-section-target="${name}"]`);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  /**
   * POST to a cart route and re-render the requested sections in one round trip.
   * @param {string} route e.g. '/cart/add.js'
   * @param {Object} body
   * @param {string[]} [sectionIds]
   * @returns {Promise<Object>} Parsed cart response.
   */
  async function postToCart(route, body, sectionIds) {
    const payload = Object.assign({}, body);

    if (sectionIds && sectionIds.length) {
      payload.sections = sectionIds;
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(route, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.description || data.message || 'Cart request failed');
      error.data = data;
      throw error;
    }

    if (data.sections) {
      Object.keys(data.sections).forEach((id) => renderSection(id, data.sections[id]));
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));

    return data;
  }

  Megatoke.cart.fetchSections = fetchSections;
  Megatoke.cart.renderSection = renderSection;
  Megatoke.cart.post = postToCart;
})();
